import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Tooltip,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Snackbar,
    CircularProgress,
    Chip,
    Menu,
    Typography,
    Avatar,
    Card,
    alpha,
    useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StoreIcon from '@mui/icons-material/Store';
import LockResetIcon from '@mui/icons-material/LockReset';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import { AddUserDialog, EditUserDialog } from './UserDialogs';
import UserBranchList from './UserBranchList';
import PageContainer from '../shared/PageContainer';

// Helper to get initials from a user
const getInitials = (firstName, lastName) => {
    return `${(firstName || '').charAt(0)}${(lastName || '').charAt(0)}`.toUpperCase();
};

// Helper to generate a consistent color from a string
const stringToColor = (str) => {
    let hash = 0;
    for (let i = 0; i < (str || '').length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colors = [
        '#1976d2', '#388e3c', '#d32f2f', '#7b1fa2',
        '#f57c00', '#0288d1', '#c2185b', '#00796b',
        '#5d4037', '#455a64'
    ];
    return colors[Math.abs(hash) % colors.length];
};

// Role color mapping
const getRoleColor = (role, theme) => {
    const map = {
        admin: { color: theme.palette.primary.main, bg: alpha(theme.palette.primary.main, 0.1), label: 'Admin' },
        branch_admin: { color: theme.palette.secondary.main, bg: alpha(theme.palette.secondary.main, 0.1), label: 'Branch Admin' },
        cashier: { color: theme.palette.info.main, bg: alpha(theme.palette.info.main, 0.1), label: 'Cashier' },
    };
    return map[role] || { color: theme.palette.grey[600], bg: alpha(theme.palette.grey[600], 0.1), label: role };
};

const UserManagement = () => {
    const theme = useTheme();
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuUser, setMenuUser] = useState(null);
    const [branchListDialog, setBranchListDialog] = useState({ open: false, user: null });
    const [resetPasswordDialog, setResetPasswordDialog] = useState({ open: false, user: null });
    const [resettingPassword, setResettingPassword] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                // Fetch users
                const usersResponse = await api.get('/auth/users');
                if (Array.isArray(usersResponse.data)) {
                    setUsers(usersResponse.data);
                } else if (usersResponse.data.users) {
                    setUsers(usersResponse.data.users);
                } else {
                    setUsers([]);
                    console.warn('Unexpected users response format:', usersResponse.data);
                }
            } catch (err) {
                console.error('Error fetching data:', err);
                setError(err.response?.data?.message || 'Error loading data');
                setUsers([]);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleAddSuccess = () => {
        showSnackbar('User added successfully');
        fetchUsers();
    };

    const fetchUsers = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await api.get('/auth/users');
            setUsers(response.data.users || []);
        } catch (err) {
            console.error('Error fetching users:', err);
            setError(err.response?.data?.message || 'Error loading users');
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleEditSuccess = (updatedUser) => {
        setUsers(users.map(user =>
            user.id === updatedUser.id ? updatedUser : user
        ));
        showSnackbar('User updated successfully');
    };

    const handleMenuClick = (event, user) => {
        setAnchorEl(event.currentTarget);
        setMenuUser(user);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuUser(null);
    };

    const handleStatusChange = async (userId, newStatus) => {
        try {
            await api.put('/auth/users/status', { userId, status: newStatus });
            fetchUsers(); // Refresh the list
            showSnackbar(`User ${newStatus === 'active' ? 'enabled' : 'disabled'} successfully`);
        } catch (error) {
            console.error('Error updating user status:', error);
            showSnackbar(error.response?.data?.message || 'Error updating user status', 'error');
        }
        handleMenuClose();
    };

    const handleViewBranches = (user) => {
        setBranchListDialog({ open: true, user });
        handleMenuClose();
    };

    const handleResetPassword = (user) => {
        setResetPasswordDialog({ open: true, user });
        handleMenuClose();
    };

    const confirmResetPassword = async () => {
        if (!resetPasswordDialog.user) return;

        setResettingPassword(true);
        try {
            const response = await api.post(`/admin/users/${resetPasswordDialog.user.id}/reset-password`);
            showSnackbar(response.data.message || 'Password reset successfully', 'success');
            setResetPasswordDialog({ open: false, user: null });
        } catch (error) {
            console.error('Error resetting password:', error);
            showSnackbar(
                error.response?.data?.message || 'Failed to reset password',
                'error'
            );
        } finally {
            setResettingPassword(false);
        }
    };

    const menuItems = (user) => [
        {
            label: 'View Branches',
            onClick: () => handleViewBranches(user),
            icon: <StoreIcon fontSize="small" />
        },
        ...(user.id !== currentUser.id ? [{
            label: 'Reset Password',
            onClick: () => handleResetPassword(user),
            icon: <LockResetIcon fontSize="small" />
        }] : []),
        {
            label: user.status === 'active' ? 'Disable' : 'Enable',
            onClick: () => handleStatusChange(user.id, user.status === 'active' ? 'disabled' : 'active'),
            icon: user.status === 'active' ? <DeleteIcon fontSize="small" /> : <EditIcon fontSize="small" />
        }
    ];

    if (loading) {
        return (
            <PageContainer title="User Management">
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            </PageContainer>
        );
    }

    if (error) {
        return (
            <PageContainer title="User Management">
                <Alert severity="error">{error}</Alert>
            </PageContainer>
        );
    }

    return (
        <PageContainer
            title="User Management"
            subtitle="Manage your team members and their access"
            actions={
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenAddDialog(true)}
                    sx={{
                        px: 3,
                        py: 1,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                        '&:hover': {
                            boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                        }
                    }}
                >
                    Add User
                </Button>
            }
        >
            {/* Summary bar */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2.5,
                    py: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                    border: '1px solid',
                    borderColor: alpha(theme.palette.primary.main, 0.12),
                }}>
                    <Avatar sx={{
                        width: 36,
                        height: 36,
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                        color: theme.palette.primary.main,
                    }}>
                        <PeopleIcon sx={{ fontSize: 20 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                            {users.length}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Total Users
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2.5,
                    py: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.success.main, 0.06),
                    border: '1px solid',
                    borderColor: alpha(theme.palette.success.main, 0.12),
                }}>
                    <Avatar sx={{
                        width: 36,
                        height: 36,
                        bgcolor: alpha(theme.palette.success.main, 0.12),
                        color: theme.palette.success.main,
                    }}>
                        <CheckCircleIcon sx={{ fontSize: 20 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                            {users.filter(u => u.status === 'active').length}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Active
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Card
                elevation={0}
                sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 3,
                    overflow: 'hidden',
                }}
            >
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{
                                bgcolor: alpha(theme.palette.primary.main, 0.03),
                            }}>
                                <TableCell sx={{
                                    fontWeight: 600,
                                    color: 'text.secondary',
                                    fontSize: '0.8rem',
                                    letterSpacing: '0.03em',
                                    textTransform: 'uppercase',
                                    py: 2,
                                    borderBottom: '2px solid',
                                    borderBottomColor: 'divider',
                                }}>Name</TableCell>
                                <TableCell sx={{
                                    fontWeight: 600,
                                    color: 'text.secondary',
                                    fontSize: '0.8rem',
                                    letterSpacing: '0.03em',
                                    textTransform: 'uppercase',
                                    py: 2,
                                    borderBottom: '2px solid',
                                    borderBottomColor: 'divider',
                                }}>Email</TableCell>
                                <TableCell sx={{
                                    fontWeight: 600,
                                    color: 'text.secondary',
                                    fontSize: '0.8rem',
                                    letterSpacing: '0.03em',
                                    textTransform: 'uppercase',
                                    py: 2,
                                    borderBottom: '2px solid',
                                    borderBottomColor: 'divider',
                                }}>Role</TableCell>
                                <TableCell sx={{
                                    fontWeight: 600,
                                    color: 'text.secondary',
                                    fontSize: '0.8rem',
                                    letterSpacing: '0.03em',
                                    textTransform: 'uppercase',
                                    py: 2,
                                    borderBottom: '2px solid',
                                    borderBottomColor: 'divider',
                                }}>Status</TableCell>
                                <TableCell sx={{
                                    fontWeight: 600,
                                    color: 'text.secondary',
                                    fontSize: '0.8rem',
                                    letterSpacing: '0.03em',
                                    textTransform: 'uppercase',
                                    py: 2,
                                    borderBottom: '2px solid',
                                    borderBottomColor: 'divider',
                                }}>Email Verified</TableCell>
                                <TableCell sx={{
                                    fontWeight: 600,
                                    color: 'text.secondary',
                                    fontSize: '0.8rem',
                                    letterSpacing: '0.03em',
                                    textTransform: 'uppercase',
                                    py: 2,
                                    borderBottom: '2px solid',
                                    borderBottomColor: 'divider',
                                }}>Last Login</TableCell>
                                <TableCell align="right" sx={{
                                    fontWeight: 600,
                                    color: 'text.secondary',
                                    fontSize: '0.8rem',
                                    letterSpacing: '0.03em',
                                    textTransform: 'uppercase',
                                    py: 2,
                                    borderBottom: '2px solid',
                                    borderBottomColor: 'divider',
                                }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((user) => {
                                const avatarColor = stringToColor(user.email || user.firstName);
                                const roleStyle = getRoleColor(user.role, theme);
                                return (
                                    <TableRow
                                        key={user.id}
                                        sx={{
                                            transition: 'background-color 0.15s ease-in-out',
                                            '&:hover': {
                                                bgcolor: alpha(theme.palette.primary.main, 0.03),
                                            },
                                            '&:last-child td': { borderBottom: 0 },
                                        }}
                                    >
                                        <TableCell sx={{ py: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar
                                                    sx={{
                                                        width: 38,
                                                        height: 38,
                                                        bgcolor: alpha(avatarColor, 0.12),
                                                        color: avatarColor,
                                                        fontSize: '0.85rem',
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {getInitials(user.firstName, user.lastName)}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                                                        {user.firstName} {user.lastName}
                                                    </Typography>
                                                    {user.id === currentUser.id && (
                                                        <Typography variant="caption" sx={{
                                                            color: theme.palette.primary.main,
                                                            fontWeight: 500,
                                                        }}>
                                                            You
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ py: 2 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {user.email}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ py: 2 }}>
                                            <Chip
                                                label={roleStyle.label}
                                                size="small"
                                                sx={{
                                                    height: 24,
                                                    fontSize: '0.75rem',
                                                    fontWeight: 600,
                                                    bgcolor: roleStyle.bg,
                                                    color: roleStyle.color,
                                                    border: 'none',
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ py: 2 }}>
                                            <Chip
                                                label={user.status === 'active' ? 'Active' : 'Inactive'}
                                                size="small"
                                                sx={{
                                                    height: 24,
                                                    fontSize: '0.75rem',
                                                    fontWeight: 600,
                                                    bgcolor: user.status === 'active'
                                                        ? alpha(theme.palette.success.main, 0.1)
                                                        : alpha(theme.palette.error.main, 0.1),
                                                    color: user.status === 'active'
                                                        ? theme.palette.success.dark
                                                        : theme.palette.error.dark,
                                                    border: 'none',
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ py: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                {user.emailVerified ? (
                                                    <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                                                ) : (
                                                    <CancelIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                                                )}
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: 500,
                                                        color: user.emailVerified
                                                            ? theme.palette.success.dark
                                                            : theme.palette.warning.dark,
                                                    }}
                                                >
                                                    {user.emailVerified ? 'Verified' : 'Pending'}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ py: 2 }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.825rem' }}>
                                                {user.lastLogin
                                                    ? new Date(user.lastLogin).toLocaleString()
                                                    : 'Never'
                                                }
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ py: 2 }}>
                                            <Tooltip title="More Actions">
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => handleMenuClick(e, user)}
                                                    sx={{
                                                        bgcolor: alpha(theme.palette.text.primary, 0.04),
                                                        '&:hover': {
                                                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                            color: theme.palette.primary.main,
                                                        },
                                                    }}
                                                >
                                                    <MoreVertIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {users.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ borderBottom: 'none' }}>
                                        <Box sx={{ py: 6 }}>
                                            <Avatar sx={{
                                                width: 64,
                                                height: 64,
                                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                                                color: theme.palette.primary.main,
                                                mx: 'auto',
                                                mb: 2,
                                            }}>
                                                <PeopleIcon sx={{ fontSize: 32 }} />
                                            </Avatar>
                                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                                No users found
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Add your first team member to get started.
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        overflow: 'visible',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.08)}`,
                        minWidth: 180,
                        mt: 0.5,
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {menuUser && menuItems(menuUser).map((item, index) => (
                    <MenuItem
                        key={index}
                        onClick={item.onClick}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            py: 1.25,
                            px: 2,
                            fontSize: '0.875rem',
                            '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.06),
                            },
                        }}
                    >
                        <Box sx={{ color: 'text.secondary', display: 'flex' }}>{item.icon}</Box>
                        <Typography variant="inherit">{item.label}</Typography>
                    </MenuItem>
                ))}
            </Menu>

            <AddUserDialog
                open={openAddDialog}
                onClose={() => setOpenAddDialog(false)}
                onSuccess={handleAddSuccess}
            />

            {openEditDialog && (
                <EditUserDialog
                    open={openEditDialog}
                    onClose={() => {
                        setOpenEditDialog(false);
                        setSelectedUser(null);
                    }}
                    user={selectedUser}
                    onSuccess={handleEditSuccess}
                />
            )}

            <UserBranchList
                open={branchListDialog.open}
                onClose={() => setBranchListDialog({ open: false, user: null })}
                user={branchListDialog.user}
            />

            {/* Password Reset Confirmation Dialog */}
            <Dialog
                open={resetPasswordDialog.open}
                onClose={() => !resettingPassword && setResetPasswordDialog({ open: false, user: null })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Reset User Password
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ py: 2 }}>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <strong>Security Notice:</strong> This action will generate a new temporary password and email it to the user.
                        </Alert>
                        <Typography variant="body1" gutterBottom>
                            Are you sure you want to reset the password for:
                        </Typography>
                        <Box sx={{
                            bgcolor: 'grey.100',
                            p: 2,
                            borderRadius: 1,
                            mt: 2,
                            border: 1,
                            borderColor: 'grey.300'
                        }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                {resetPasswordDialog.user?.firstName} {resetPasswordDialog.user?.lastName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {resetPasswordDialog.user?.email}
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            The user will receive an email with a temporary password and will be required to change it on their next login.
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setResetPasswordDialog({ open: false, user: null })}
                        disabled={resettingPassword}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmResetPassword}
                        variant="contained"
                        color="warning"
                        disabled={resettingPassword}
                        startIcon={resettingPassword ? <CircularProgress size={16} /> : <LockResetIcon />}
                    >
                        {resettingPassword ? 'Resetting...' : 'Reset Password'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </PageContainer>
    );
};

export default UserManagement;
